import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import SearchIcon from '@mui/icons-material/Search';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  useQuarantineList,
  useRetryDocument,
  useRejectDocument,
  defaultQuarantineFilters,
  type QuarantineFilters,
  type QuarantineDocument,
} from '../hooks/useQuarantine';
import { useRole, hasMinimumRole } from '../hooks/useRole';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/admin';

const statusOptions = [
  { value: '', label: 'Alle statuser' },
  { value: 'failed', label: 'Feilet' },
  { value: 'pending', label: 'Ventende' },
];

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('nb-NO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function statusChipColor(status: string): 'error' | 'warning' | 'default' {
  if (status === 'failed') return 'error';
  if (status === 'pending') return 'warning';
  return 'default';
}

export function QuarantinePage() {
  const role = useRole();
  const [filters, setFilters] = useState<QuarantineFilters>(defaultQuarantineFilters);
  const [rejectTarget, setRejectTarget] = useState<QuarantineDocument | null>(null);

  const { data, isLoading, error } = useQuarantineList(filters);
  const retryMutation = useRetryDocument();
  const rejectMutation = useRejectDocument();

  const updateFilter = (partial: Partial<QuarantineFilters>) =>
    setFilters((prev) => ({ ...prev, ...partial, page: partial.page ?? 1 }));

  const columns: GridColDef[] = [
    { field: 'Filnavn_original', headerName: 'Filnavn', flex: 1, minWidth: 180 },
    { field: 'AvtaleTittel', headerName: 'Avtale', width: 160, valueFormatter: (value: string | null) => value || '-' },
    {
      field: 'Analyse_status', headerName: 'Status', width: 110,
      renderCell: (params) => (
        <Chip label={params.value === 'failed' ? 'Feilet' : params.value === 'pending' ? 'Ventende' : params.value}
              size="small" color={statusChipColor(params.value)} />
      ),
    },
    {
      field: 'Analyse_feilmelding', headerName: 'Feilmelding', width: 220,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value || ''}>{params.value || '-'}</Typography>
      ),
    },
    { field: 'Lastet_opp_av', headerName: 'Opplastet av', width: 130, valueFormatter: (value: string | null) => value || '-' },
    { field: 'Lastet_opp_dato', headerName: 'Dato', width: 150, valueFormatter: (value: string | null) => formatDate(value) },
    {
      field: 'actions', headerName: 'Tiltak', width: 220, sortable: false,
      renderCell: (params) => {
        const doc = params.row as QuarantineDocument;
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => window.open(`${API_BASE}/documents/${doc.DokumentID}/content`, '_blank')}>
              Vis
            </Button>
            {hasMinimumRole(role, 'Editor') && doc.Analyse_status === 'failed' && (
              <Button size="small" variant="outlined" color="primary"
                disabled={retryMutation.isPending}
                onClick={() => retryMutation.mutate(doc.DokumentID)}>
                {retryMutation.isPending ? '...' : 'Prøv igjen'}
              </Button>
            )}
            {hasMinimumRole(role, 'Admin') && (
              <Button size="small" variant="outlined" color="error"
                disabled={rejectMutation.isPending}
                onClick={() => setRejectTarget(doc)}>
                Avvis
              </Button>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1 }}>Karantene</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Dokumenter som har feilet under AI-analyse eller venter på reanalyse.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Søk etter filnavn..."
          value={filters.search}
          onChange={(e) => updateFilter({ search: e.target.value })}
          sx={{ width: 280 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="quarantine-status-label">Status</InputLabel>
          <Select labelId="quarantine-status-label" value={filters.status} label="Status" onChange={(e) => updateFilter({ status: e.target.value })}>
            {statusOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Kunne ikke laste karantene-liste: {error instanceof Error ? error.message : 'Ukjent feil'}
        </Alert>
      )}

      <DataGrid
        rows={data?.data ?? []}
        columns={columns}
        getRowId={(row) => row.DokumentID}
        loading={isLoading}
        paginationMode="server"
        rowCount={data?.pagination?.totalCount ?? 0}
        paginationModel={{ page: filters.page - 1, pageSize: data?.pagination?.pageSize ?? 50 }}
        onPaginationModelChange={(model) => setFilters((prev) => ({ ...prev, page: model.page + 1 }))}
        pageSizeOptions={[25, 50]}
        disableRowSelectionOnClick
        autoHeight
        sx={{ bgcolor: 'background.paper' }}
      />

      {/* Reject confirmation dialog */}
      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)}>
        <DialogTitle>Avvis dokument</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Er du sikker på at du vil avvise &ldquo;{rejectTarget?.Filnavn_original}&rdquo;? Dokumentet vil ikke bli reanalysert.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Avbryt</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (rejectTarget) {
                rejectMutation.mutate(rejectTarget.DokumentID);
                setRejectTarget(null);
              }
            }}
          >
            Avvis
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
